import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Maximize2, Minimize2, PlaneTakeoff, PlaneLanding } from 'lucide-react';
import type {
  ColumbusPlannerPassengerModel,
  ColumbusPlannerTermModel,
} from '@/api/generated/schemas';
import { Button, AsyncActionButton, Dialog, Panel } from '@/shared/ui';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/shadcn';
import { cn } from '@/shared/lib/utils';
import { useLegacyMessaging } from '@/shared/lib/legacyMagic';
import { useColumbusStore } from '../../columbusStore';
import {
  useLoadColumbusPlannerTerm,
  useLoadColumbusPlannerTermFlightRoutes,
} from '@/api/generated/columbus-planner/columbus-planner';
import { FlightRouteVisualization } from '../../flights/components/FlightRouteVisualization';

export type Direction = 'outbound' | 'inbound' | 'roundtrip';

export interface EligibleGroup {
  businessCaseNumber: string | null;
  bcPersonIds: number[];
  names: string[];
}

interface Props {
  term: ColumbusPlannerTermModel;
  productId: number;
  destinationSeasonId: number;
  termsSerialId: number;
  onClose: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getEligiblePassengers(term: ColumbusPlannerTermModel, direction: Direction) {
  return (term.passengers ?? []).filter((p) => {
    if (direction === 'outbound') return p.depTicketAllocationId === null;
    if (direction === 'inbound') return p.retTicketAllocationId === null;
    if (direction === 'roundtrip')
      return p.depTicketAllocationId === null && p.retTicketAllocationId === null;
  });
}

// Group eligible passengers by businessCaseNumber so persons from the same OP appear together
// in the UI. Each group still tracks individual bcPersonIds for selection. Passengers without
// a businessCaseNumber are listed as separate groups (one person each) so they remain visible.
// eslint-disable-next-line react-refresh/only-export-components
export function groupEligiblePassengers(
  eligible: ColumbusPlannerPassengerModel[]
): EligibleGroup[] {
  const groups: EligibleGroup[] = [];
  const indexByBusinessCase = new Map<string, number>();
  eligible.forEach((p, idx) => {
    const displayName = p.name || `(${idx + 1})`;
    if (p.businessCaseNumber != null) {
      const existing = indexByBusinessCase.get(p.businessCaseNumber);
      if (existing != null) {
        groups[existing]!.names.push(displayName);
        groups[existing]!.bcPersonIds.push(p.bcPersonId);
      } else {
        indexByBusinessCase.set(p.businessCaseNumber, groups.length);
        groups.push({
          businessCaseNumber: p.businessCaseNumber,
          bcPersonIds: [p.bcPersonId],
          names: [displayName],
        });
      }
    } else {
      groups.push({
        businessCaseNumber: null,
        bcPersonIds: [p.bcPersonId],
        names: [displayName],
      });
    }
  });
  return groups;
}

const FIELD_LABEL_CLASS = 'text-muted-foreground w-32 shrink-0 pt-1 text-right text-xs font-medium';
const INPUT_CLASS = 'rounded border border-phx-border-2 bg-phx-surface px-1.5 py-0.5 text-xs';

const dateFormatter = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return dateFormatter.format(d);
}

function addDays(value: string, days: number): string {
  const d = new Date(value);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

function ExpandToggle({
  expanded,
  onToggle,
  expandLabel,
  collapseLabel,
}: {
  expanded: boolean;
  onToggle: () => void;
  expandLabel: string;
  collapseLabel: string;
}) {
  const label = expanded ? collapseLabel : expandLabel;
  const Icon = expanded ? Minimize2 : Maximize2;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-pressed={expanded}
      title={label}
      className="text-muted-foreground hover:text-foreground hover:bg-phx-surface-2 inline-flex h-6 w-6 items-center justify-center rounded transition-colors"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

function FlightPanelMeta({ date, code }: { date: string; code: string | null | undefined }) {
  return (
    <span className="text-foreground flex items-baseline gap-2 text-sm font-medium">
      <span>{date}</span>
      {code && (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="font-mono">{code}</span>
        </>
      )}
    </span>
  );
}

export function BuyTicketsDialog({
  term,
  productId,
  destinationSeasonId,
  termsSerialId,
  onClose,
}: Props) {
  const { t } = useTranslation('columbus-planner');
  const { send, isPending } = useLegacyMessaging();
  const updateTerm = useColumbusStore((s) => s.updateTerm);

  const [direction, setDirection] = useState<Direction>('roundtrip');
  const [count, setCount] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [returnCost, setReturnCost] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [isReloading, setIsReloading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isFlightRouteExpanded, setIsFlightRouteExpanded] = useState(false);

  const eligiblePassengers = useMemo(
    () => getEligiblePassengers(term, direction),
    [term, direction]
  );

  const eligibleGroups = useMemo(
    () => groupEligiblePassengers(eligiblePassengers),
    [eligiblePassengers]
  );

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  // Re-initialise selection when direction changes
  useEffect(() => {
    const eligible = getEligiblePassengers(term, direction);
    setSelectedIds(
      (prev) => new Set(eligible.filter((p) => prev.has(p.bcPersonId)).map((p) => p.bcPersonId))
    );
  }, [direction, term]);

  const { data: reloadedTerm, isFetching, refetch } = useLoadColumbusPlannerTerm(term.id);

  const { data: flightRoutes, isFetching: isFetchingRoutes } =
    useLoadColumbusPlannerTermFlightRoutes(term.id);

  useEffect(() => {
    if (isReloading && !isFetching && reloadedTerm) {
      updateTerm(reloadedTerm);
      onClose();
    }
  }, [isReloading, isFetching, reloadedTerm, updateTerm, onClose]);

  const isBlocked = isPending || isReloading;
  const showOutboundCost = direction !== 'inbound';
  const showInboundCost = direction !== 'outbound';
  const outboundActive = direction !== 'inbound';
  const inboundActive = direction !== 'outbound';
  const isCountValid = count != null && count > 0;
  const isCodeValid = code.trim().length > 0;
  // Cost: pro outbound/roundtrip kontrolujeme `cost` (= outbound cena);
  // pro inbound se cost používá jako inbound cena.
  const isCostValid = cost != null && cost > 0;
  const canSubmit = isCountValid && isCodeValid && isCostValid;

  const togglePassenger = (bcPersonId: number, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(bcPersonId);
    else next.delete(bcPersonId);
    setSelectedIds(next);
    if (checked) {
      // Auto-bump count to at least cover the selected passengers; never lower a manually set count.
      setCount((c) => Math.max(c ?? 0, next.size));
    }
  };

  const toggleGroup = (group: EligibleGroup, checked: boolean) => {
    const next = new Set(selectedIds);
    for (const id of group.bcPersonIds) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    setSelectedIds(next);
    if (checked) {
      setCount((c) => Math.max(c ?? 0, next.size));
    }
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    setSaveError(null);
    send(
      'columbus-planner/message',
      {
        productId,
        destinationSeasonId,
        termsSerialId,
        action: 'newticket',
        newTicket: {
          termId: term.id,
          isDeparture: direction !== 'inbound',
          isReturn: direction !== 'outbound',
          count: count ?? 0,
          cost,
          returnCost,
          code,
          prioritizedBcPersonIds: [...selectedIds],
        },
      },
      (response) => {
        if (response.error) {
          setSaveError(response.error);
        } else {
          setIsReloading(true);
          void refetch();
        }
      }
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('buyTickets.title')}
      width="xl"
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isBlocked}>
            {t('buyTickets.cancel')}
          </Button>
          <AsyncActionButton
            isRunning={isPending}
            onClick={handleConfirm}
            disabled={(isBlocked && !isPending) || !canSubmit}
            runningLabel={t('buyTickets.saving')}
            doneLabel={t('buyTickets.saved')}
          >
            {t('buyTickets.confirm')}
          </AsyncActionButton>
        </>
      }
    >
      {/* Blocked overlay while the term reloads after a successful purchase */}
      {isBlocked && (
        <div className="bg-phx-surface/60 fixed inset-0 z-10000 flex items-center justify-center">
          {isReloading && <Loader2 className="text-phx-text-muted h-8 w-8 animate-spin" />}
        </div>
      )}

      {/* Body */}
      <div className="flex flex-col gap-4">
        {/* Settings panel — no title (duplicates window title) */}
        <Panel>
          <div className="space-y-2.5">
            {/* Direction */}
            <div className="flex items-center gap-3">
              <span className={FIELD_LABEL_CLASS}>{t('buyTickets.direction')}</span>
              <div className="flex-1">
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={direction}
                  onValueChange={(v) => {
                    if (v) setDirection(v as Direction);
                  }}
                >
                  {(['outbound', 'inbound', 'roundtrip'] as Direction[]).map((d) => (
                    <ToggleGroupItem key={d} value={d} aria-label={t(`buyTickets.${d}`)}>
                      {t(`buyTickets.${d}`)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </div>

            {/* Count */}
            <div className="flex items-center gap-3">
              <label htmlFor="buy-count" className={FIELD_LABEL_CLASS}>
                {t('buyTickets.count')} <span className="text-phx-danger">*</span>
              </label>
              <div className="flex-1">
                <input
                  id="buy-count"
                  type="number"
                  min={1}
                  required
                  aria-required="true"
                  className={cn(INPUT_CLASS, 'bg-field-required w-20 text-right')}
                  value={count ?? ''}
                  onChange={(e) => setCount(e.target.value === '' ? null : Number(e.target.value))}
                />
              </div>
            </div>

            {/* Cost — single row, one or two inputs depending on direction */}
            <div className="flex items-center gap-3">
              <span className={FIELD_LABEL_CLASS}>
                {t('buyTickets.costPerPax')} <span className="text-phx-danger">*</span>
              </span>
              <div className="flex flex-1 flex-wrap items-center gap-4">
                {showOutboundCost && (
                  <div className="relative">
                    <PlaneTakeoff
                      className="text-phx-text-muted pointer-events-none absolute top-1/2 left-1.5 h-3.5 w-3.5 -translate-y-1/2"
                      aria-label={t('buyTickets.outboundCostPerPax')}
                    />
                    <input
                      id="buy-cost"
                      type="number"
                      required
                      aria-required="true"
                      className={cn(INPUT_CLASS, 'bg-field-required w-24 pl-6 text-right')}
                      value={cost ?? ''}
                      onChange={(e) =>
                        setCost(e.target.value === '' ? null : Number(e.target.value))
                      }
                    />
                  </div>
                )}
                {showInboundCost && (
                  <div className="relative">
                    <PlaneLanding
                      className="text-phx-text-muted pointer-events-none absolute top-1/2 left-1.5 h-3.5 w-3.5 -translate-y-1/2"
                      aria-label={t('buyTickets.returnCostPerPax')}
                    />
                    <input
                      id="buy-return-cost"
                      type="number"
                      required={direction === 'inbound'}
                      aria-required={direction === 'inbound'}
                      className={cn(
                        INPUT_CLASS,
                        'w-24 pl-6 text-right',
                        direction === 'inbound' && 'bg-field-required'
                      )}
                      value={direction === 'inbound' ? (cost ?? '') : (returnCost ?? '')}
                      placeholder={direction === 'roundtrip' && cost != null ? String(cost) : ''}
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : Number(e.target.value);
                        if (direction === 'inbound') setCost(v);
                        else setReturnCost(v);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Code */}
            <div className="flex items-center gap-3">
              <label htmlFor="buy-code" className={FIELD_LABEL_CLASS}>
                {t('buyTickets.code')} <span className="text-phx-danger">*</span>
              </label>
              <div className="flex-1">
                <input
                  id="buy-code"
                  type="text"
                  required
                  aria-required="true"
                  className={cn(INPUT_CLASS, 'bg-field-required w-40')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
            </div>

            {/* Passengers */}
            <div className="flex items-start gap-3">
              <span className={FIELD_LABEL_CLASS}>{t('buyTickets.passengers')}</span>
              <div className="flex-1">
                {eligibleGroups.length > 0 ? (
                  <div className="border-phx-border bg-phx-surface max-h-48 overflow-y-auto rounded border p-1.5">
                    {eligibleGroups.map((g, idx) => {
                      const groupKey = g.businessCaseNumber ?? `none-${idx}`;
                      const allChecked = g.bcPersonIds.every((id) => selectedIds.has(id));
                      const someChecked =
                        !allChecked && g.bcPersonIds.some((id) => selectedIds.has(id));
                      return (
                        <div
                          key={groupKey}
                          className={cn(
                            'flex items-start gap-3 py-1 text-xs',
                            idx > 0 && 'border-phx-border border-t'
                          )}
                        >
                          {g.businessCaseNumber != null ? (
                            <label className="flex w-28 shrink-0 cursor-pointer items-center gap-1.5 font-medium">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => {
                                  if (el) el.indeterminate = someChecked;
                                }}
                                onChange={(e) => toggleGroup(g, e.target.checked)}
                              />
                              <span className="text-phx-text-2 font-mono">
                                {g.businessCaseNumber}
                              </span>
                            </label>
                          ) : (
                            <span className="w-28 shrink-0" />
                          )}
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            {g.bcPersonIds.map((id, i) => (
                              <label key={id} className="flex cursor-pointer items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(id)}
                                  onChange={(e) => togglePassenger(id, e.target.checked)}
                                />
                                <span>{g.names[i]}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-phx-text-muted py-1 text-xs">{t('buyTickets.noEligible')}</p>
                )}
              </div>
            </div>

            {saveError && <p className="text-phx-danger text-xs">{saveError}</p>}
          </div>
        </Panel>

        {/* Flight panels — side-by-side v kompaktním režimu, stacked při expanzi.
              "Expand" tlačítko ovládá oba směry najednou. */}
        <div
          className={cn(
            'grid gap-4',
            isFlightRouteExpanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
          )}
        >
          {/* Outbound flight — always visible, muted when inactive */}
          <Panel
            className={cn(
              'transition-opacity',
              !outboundActive && 'pointer-events-none opacity-40 grayscale'
            )}
            title={
              <span className="flex items-center gap-2">
                <PlaneTakeoff className="text-phx-text-muted h-4 w-4" aria-hidden="true" />
                {t('buyTickets.flightRoute.outbound')}
                <ExpandToggle
                  expanded={isFlightRouteExpanded}
                  onToggle={() => setIsFlightRouteExpanded((v) => !v)}
                  expandLabel={t('buyTickets.flightRoute.expand')}
                  collapseLabel={t('buyTickets.flightRoute.collapse')}
                />
              </span>
            }
            actions={
              <FlightPanelMeta
                date={formatDate(term.dateFrom)}
                code={flightRoutes?.outbound?.code}
              />
            }
          >
            <FlightRouteVisualization
              route={flightRoutes?.outbound ?? null}
              isLoading={isFetchingRoutes}
              compact={!isFlightRouteExpanded}
            />
          </Panel>

          {/* Inbound flight — always visible, muted when inactive */}
          <Panel
            className={cn(
              'transition-opacity',
              !inboundActive && 'pointer-events-none opacity-40 grayscale'
            )}
            title={
              <span className="flex items-center gap-2">
                <PlaneLanding className="text-phx-text-muted h-4 w-4" aria-hidden="true" />
                {t('buyTickets.flightRoute.inbound')}
                <ExpandToggle
                  expanded={isFlightRouteExpanded}
                  onToggle={() => setIsFlightRouteExpanded((v) => !v)}
                  expandLabel={t('buyTickets.flightRoute.expand')}
                  collapseLabel={t('buyTickets.flightRoute.collapse')}
                />
              </span>
            }
            actions={
              <FlightPanelMeta
                date={formatDate(addDays(term.dateFrom, term.nights ?? 0))}
                code={flightRoutes?.inbound?.code}
              />
            }
          >
            <FlightRouteVisualization
              route={flightRoutes?.inbound ?? null}
              isLoading={isFetchingRoutes}
              compact={!isFlightRouteExpanded}
            />
          </Panel>
        </div>
      </div>
    </Dialog>
  );
}
